import { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { processQuestionHtmlForRender, extractImageUrls } from '../utils/questionHtmlProcessor';
import { watchImageHeight } from '../utils/imageHeightLimit';
import ImageLightbox from './ImageLightbox';

interface QuestionHtmlWithZoomProps {
  html: string;
  imageWidths?: number[];
  onWidthChange?: (newWidths: number[]) => void;
  isPreview?: boolean;
  /**
   * @deprecated 历史遗留参数，不再支持图片缩放交互。
   * quiz 模式现在纯展示，与编辑器 1:1 复刻。
   */
  questionType?: string;
}

/**
 * 题目 HTML 渲染组件（纯展示）
 *
 * 设计目标：与编辑模式（QuillEditorWithImageResize）1:1 复刻渲染效果。
 * 两端共用 processQuestionHtmlForRender 函数处理 HTML，确保：
 * - 图片 URL 规范化一致
 * - 空段落清理一致
 * - 连续 <br> 压缩一致
 * - 图文分行规则一致
 * - width% 注入一致
 *
 * 不再支持图片缩放交互（点击图片弹出面板），quiz 模式纯展示。
 */
export default function QuestionHtmlWithZoom({
  html,
  imageWidths = [],
  isPreview = false,
}: QuestionHtmlWithZoomProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // 统一处理 HTML（与编辑器走同一份函数）
  const processedHtml = useMemo(() => {
    return processQuestionHtmlForRender(html, imageWidths);
  }, [html, imageWidths]);

  // 提取图片 URL（用于判断是否有图片）
  const imageUrls = useMemo(() => extractImageUrls(html), [html]);

  // 图片高度限制：基于容器宽度动态计算 max-height（与编辑模式一致）
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let cleanupFn: (() => void) | null = null;
    let cancelled = false;

    const rafId = requestAnimationFrame(() => {
      if (cancelled) return;
      try {
        cleanupFn = watchImageHeight(container);
      } catch (error) {
        console.warn('Failed to initialize image height observer:', error);
      }
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      if (cleanupFn) cleanupFn();
    };
  }, [html]);

  // 图片加载优化：为 HTML 内嵌图片添加 decoding=async 和淡入动画
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const images = container.querySelectorAll('img');
    const listeners: Array<{ img: HTMLImageElement; fn: () => void }> = [];

    images.forEach(img => {
      img.decoding = 'async';
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add('img-html-loaded');
      } else {
        const fn = () => { img.classList.add('img-html-loaded'); };
        img.addEventListener('load', fn, { once: true });
        listeners.push({ img, fn });
      }
    });

    return () => {
      listeners.forEach(({ img, fn }) => {
        img.removeEventListener('load', fn);
      });
    };
  }, [processedHtml]);

  // 图片点击放大预览
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const closeLightbox = useCallback(() => setLightboxSrc(null), []);

  // 事件委托：监听容器内 img 点击
  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'IMG') {
      const img = target as HTMLImageElement;
      if (img.src) {
        e.preventDefault();
        setLightboxSrc(img.src);
      }
    }
  }, []);

  // 为容器内 img 添加 cursor-pointer 样式
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const images = container.querySelectorAll('img');
    images.forEach(img => {
      img.style.cursor = 'pointer';
    });
  }, [processedHtml]);

  // 统一渲染：无论是否有图片，都用同一套处理逻辑
  // 静默 isPreview 参数（保留接口兼容性，但不再影响渲染）
  void isPreview;

  return (
    <>
      <div
        ref={containerRef}
        className="question-html-content max-w-none"
        style={{ whiteSpace: 'pre-line' }}
        dangerouslySetInnerHTML={{ __html: processedHtml }}
        onClick={handleClick}
      />
      {lightboxSrc && <ImageLightbox src={lightboxSrc} onClose={closeLightbox} />}
    </>
  );
}
